import { CommonModule, registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { EnvironmentProviders, LOCALE_ID, ModuleWithProviders, NgModule, Provider } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { RouterModule } from '@angular/router';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { CookieService } from "ngx-cookie-service";
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ActionBarComponent } from './components/action-bar/action-bar.component';
import { DividedCardComponent } from './components/divided-card/divided-card.component';
import { EnvBadgeComponent } from './components/env-badge/env-badge.component';
import { FileComponent } from './components/file/file.component';
import { HelpButtonComponent } from './components/help-button/help-button.component';
import { ImageComponent } from './components/image/image.component';
import { LoggedUserAvatarComponent } from './components/logged-user-avatar/logged-user-avatar.component';
import { getPaginatoriIntl } from './components/mat-paginator.intl';
import { ConfirmModalComponent } from './components/modals/confirm-modal/confirm-modal.component';
import { ModalRemoveComponent } from './components/modals/modal-remove/modal-remove.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { PdfContainerComponent } from './components/pdf-container/pdf-container.component';
import { SelectListComponent } from './components/select-list/select-list.component';
import { SelectSearchComponent } from './components/select-search/select-search.component';
import { UpSessionComponent } from './components/session/session.component';
import { TableComponent } from './components/table/table.component';
import { ImageOnStockDirective } from './directives/image.directive';
import { MaterialModule } from './modules/material.module';
import { SearchPipe } from './pipes/search-pipe/search.pipe';

registerLocaleData(localePt);

const CUSTOM_DATE_FORMAT = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },

  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export interface NgxEssentialsModuleConfig {
  apiGatewayUrl: string;
  appWorkspaceUrl: string;
  appName: string;
  doneAppDomain?: string;
}

@NgModule({
  declarations: [
    // Pipes
    SearchPipe,

    // Directives
    ImageOnStockDirective,
    SelectSearchComponent,

    //Components
    TableComponent,
    ActionBarComponent,
    HelpButtonComponent,
    FileComponent,
    ImageComponent,
    NotFoundComponent,
    PdfContainerComponent,
    SelectListComponent,
    SelectSearchComponent,
    ConfirmModalComponent,
    ModalRemoveComponent,
    UpSessionComponent,
    DividedCardComponent,
    LoggedUserAvatarComponent,
    EnvBadgeComponent
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    PdfViewerModule,
    NgxMatSelectSearchModule,
  ],
  exports: [
    //Modules
    MaterialModule,
    PdfViewerModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    RouterModule,
    // Components
    SelectSearchComponent,
    TableComponent,
    ActionBarComponent,
    HelpButtonComponent,
    FileComponent,
    ImageComponent,
    NotFoundComponent,
    PdfContainerComponent,
    SelectListComponent,
    ConfirmModalComponent,
    ModalRemoveComponent,
    UpSessionComponent,
    DividedCardComponent,
    LoggedUserAvatarComponent,
    EnvBadgeComponent,
    //Modules
    MaterialModule,
    PdfViewerModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    RouterModule,
    NgxMatSelectSearchModule,

    // Pipes
    SearchPipe,

    // Directives
    ImageOnStockDirective,
  ],
})
export class NgxEssentialsModule {
  static forRoot(config?: NgxEssentialsModuleConfig): ModuleWithProviders<NgxEssentialsModule> {
    const providers: (Provider | EnvironmentProviders)[] = [
      { provide: MAT_DATE_LOCALE, useValue: 'pt-Br' },
      { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMAT },
      { provide: LOCALE_ID, useValue: 'pt-Br' },
      { provide: DateAdapter, useClass: MomentDateAdapter },
      { provide: MatPaginatorIntl, useValue: getPaginatoriIntl() },
      { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 3500 } },
      { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: true, disableClose: false, panelClass: ['col-12', 'col-md-6', 'col-lg-4'] } },
      { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'always' } },
      provideEnvironmentNgxMask(),
      CookieService
    ];

    if (!!config) {
      providers.push({ provide: 'API_GATEWAY_URL', useValue: config.apiGatewayUrl })
      providers.push({ provide: 'APP_WORKSPACE_URL', useValue: config.appWorkspaceUrl })
      providers.push({ provide: 'DONE_APP_DOMAIN', useValue: config.doneAppDomain })
      providers.push({ provide: 'VUP_APP_NAME', useValue: config.appName })

      this.startIframePostMessageBridge(config.appWorkspaceUrl);
    }

    return {
      ngModule: NgxEssentialsModule,
      providers: providers,
    }
  }

  private static startIframePostMessageBridge(parentOrigin: string) {
    if (window.parent === window) return;

    const notify = () => {
      window.parent.postMessage(
        { type: 'iframe:navigation', url: window.location.href },
        parentOrigin
      );
    };

    // inicial
    notify();

    // back/forward
    window.addEventListener('popstate', notify);
    window.addEventListener('hashchange', notify);

    // hook pushState/replaceState
    const originalPushState = history.pushState.bind(history);
    history.pushState = (...args: Parameters<History['pushState']>) => {
      const ret = originalPushState(...args);
      notify();
      return ret;
    };

    const originalReplaceState = history.replaceState.bind(history);
    history.replaceState = (...args: Parameters<History['replaceState']>) => {
      const ret = originalReplaceState(...args);
      notify();
      return ret;
    };
  }
}